import type { ProjectSummary } from '@shared/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, Play } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/ui/button';
import { Card, CardBody, CardFooter, CardHeader } from '../../components/ui/card';
import { Alert } from '../../components/ui/feedback';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query';
import { errorMessage } from '../../lib/session';
import { StepAttributes } from './step-attributes';
import { StepReview } from './step-review';
import { StepRules } from './step-rules';
import { StepSearch } from './step-search';
import { StepSetup } from './step-setup';
import { StepSources } from './step-sources';
import { REVIEW_STEP_INDEX, useWizard, VISIBLE_STEP_COUNT, WIZARD_STEPS } from './use-wizard';

/** POST /api/projects returns the created project plus the id of the run it started, if any. */
interface CreateProjectResponse {
  project: ProjectSummary;
  jobId: string | null;
}

/** The research wizard: Sources → Source setup → Search → Attributes → Rules → Review → Run. */
export function WizardPage() {
  const wizard = useWizard();
  const navigate = useNavigate();

  const client = useQueryClient();
  const create = useMutation({
    mutationFn: (start: boolean) => api.post<CreateProjectResponse>('/api/projects', { config: wizard.config, start }),
    onSuccess: async (result) => {
      await client.invalidateQueries({ queryKey: queryKeys.projects });
      void navigate(`/research/${result.project.id}`);
    },
  });

  const isReview = wizard.step === 'review';
  const isLastBeforeReview = wizard.stepIndex === REVIEW_STEP_INDEX - 1;

  return (
    <div className="mx-auto max-w-[1000px] px-5 py-5">
      <header className="mb-4">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">New research</h1>
        <p className="mt-0.5 text-[13px] text-muted-foreground">Define what to collect, then review it before anything runs.</p>
      </header>

      <ol className="mb-4 flex flex-wrap gap-1" aria-label="Progress">
        {WIZARD_STEPS.slice(0, VISIBLE_STEP_COUNT).map((step, index) => {
          const done = index < wizard.stepIndex;
          const current = index === wizard.stepIndex;
          return (
            <li key={step.id}>
              <button
                type="button"
                disabled={index > wizard.stepIndex}
                onClick={() => wizard.setStep(index)}
                aria-current={current ? 'step' : undefined}
                className={cn(
                  'flex items-center gap-1.5 rounded-[var(--radius-control)] px-2.5 py-1.5 text-[13px] font-medium transition-colors duration-150',
                  current ? 'bg-accent-subtle text-accent' : done ? 'text-foreground hover:bg-muted' : 'text-muted-foreground',
                  index > wizard.stepIndex && 'cursor-not-allowed',
                )}
              >
                <span
                  className={cn(
                    'tabular flex h-4 w-4 items-center justify-center rounded-full text-[10px]',
                    current ? 'bg-accent text-accent-foreground' : done ? 'bg-success text-white' : 'bg-muted text-muted-foreground',
                  )}
                >
                  {done ? <Check className="h-2.5 w-2.5" aria-hidden="true" /> : index + 1}
                </span>
                {step.label}
              </button>
            </li>
          );
        })}
      </ol>

      <Card>
        <CardHeader
          title={WIZARD_STEPS[wizard.stepIndex]?.label ?? ''}
          description={WIZARD_STEPS[wizard.stepIndex]?.description}
        />
        <CardBody className="py-4">
          {wizard.step === 'sources' ? (
            <StepSources
              selected={wizard.config.sources ?? []}
              onChange={wizard.setSources}
              error={wizard.stepErrors['sources']}
            />
          ) : null}

          {wizard.step === 'setup' ? (
            <StepSetup config={wizard.config} onChange={wizard.update} errors={wizard.stepErrors} />
          ) : null}

          {wizard.step === 'search' ? (
            <StepSearch config={wizard.config} onChange={wizard.update} errors={wizard.stepErrors} />
          ) : null}

          {wizard.step === 'attributes' ? (
            <StepAttributes
              sources={wizard.config.sources ?? []}
              websiteEnrichment={wizard.config.enrichment?.website?.enabled ?? true}
              selected={wizard.config.attributes?.selected ?? []}
              custom={(wizard.config.attributes?.custom ?? []).map((attribute) => ({
                ...attribute,
                aiAssisted: attribute.aiAssisted ?? true,
              }))}
              onSelectedChange={(selected) =>
                wizard.update({ attributes: { selected, custom: wizard.config.attributes?.custom ?? [] } })
              }
              onCustomChange={(custom) =>
                wizard.update({ attributes: { selected: wizard.config.attributes?.selected ?? [], custom } })
              }
              errors={wizard.stepErrors}
            />
          ) : null}

          {wizard.step === 'rules' ? (
            <StepRules config={wizard.config} onChange={wizard.update} errors={wizard.stepErrors} />
          ) : null}

          {isReview ? <StepReview config={wizard.config} /> : null}

          {create.error ? (
            <Alert className="mt-4" tone="error">
              {errorMessage(create.error)}
            </Alert>
          ) : null}
        </CardBody>

        <CardFooter className="justify-between">
          <Button variant="ghost" icon={<ArrowLeft className="h-3.5 w-3.5" />} disabled={wizard.stepIndex === 0} onClick={wizard.back}>
            Back
          </Button>
          {isReview ? (
            <span className="flex items-center gap-2">
              <Button variant="outline" loading={create.isPending && !create.variables} onClick={() => create.mutate(false)}>
                Save without running
              </Button>
              <Button icon={<Play className="h-3.5 w-3.5" />} loading={create.isPending} onClick={() => create.mutate(true)}>
                Start research
              </Button>
            </span>
          ) : (
            <Button
              icon={<ArrowRight className="h-3.5 w-3.5" />}
              disabled={!wizard.canAdvance}
              onClick={wizard.next}
              title={wizard.canAdvance ? undefined : Object.values(wizard.stepErrors)[0]}
            >
              {isLastBeforeReview ? 'Review' : 'Continue'}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
